import { Injectable } from '@angular/core';
import { BaseDownloadServiceWithFilter } from './base.service';
import { Invoice } from '@cl/shared/models/invoices/invoice.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Client, CommandResult } from '@cl/shared/models';
import { FileMetadata } from '@cl/shared/models/file.model';
import { lastValueFrom, Observable } from 'rxjs';
import { NzImageService } from 'ng-zorro-antd/image';
import { DashboardInvoiceStats } from '@cl/shared/models/invoices/dashboard-invoice-stats.model';
import { InvoiceStats } from '@cl/shared/models/invoices/invoice-stats.model';
import { InvoiceFilter } from '@cl/shared/models/invoices/invoice-filter.model';
import { InvoiceClientNotificationConfiguration } from '@cl/shared/models/invoices/invoice-client-notification-configuration.model';

@Injectable({
    providedIn: 'root',
})
export class InvoicesService extends BaseDownloadServiceWithFilter<Invoice, InvoiceFilter> {
    constructor(
        protected httpClient: HttpClient,
        protected nzImageService: NzImageService,
    ) {
        super(httpClient, '/api/v1/invoices/', nzImageService);
    }

    async getInvoiceFileMetadata(invoiceId: number): Promise<CommandResult<FileMetadata>> {
        return lastValueFrom(this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}download/${invoiceId}`));
    }

    async downloadInvoice(invoiceId: number) {
        return this.handleFileDownloading(this.getInvoiceFileMetadata(invoiceId));
    }

    async downloadInvoiceEInvoiceXML(invoiceId: number) {
        return this.handleGeneratedFileDownload(
            lastValueFrom(
                this.httpClient.get<Blob>(`${this.baseApiUrl}download-e-invoice/${invoiceId}`, {
                    responseType: 'blob' as 'json',
                    observe: 'response',
                }),
            ),
        );
    }

    async sendInvoiceNotification(invoiceNotificationConfig: InvoiceClientNotificationConfiguration): Promise<void> {
        return await lastValueFrom(this.httpClient.post<void>(`${this.baseApiUrl}send-notification`, invoiceNotificationConfig));
    }

    async getInvoiceClient(invoiceId: number): Promise<CommandResult<Client>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<Client>>(`${this.baseApiUrl}${invoiceId}/client`));
    }

    async getInvoiceStatsForDashboard(): Promise<CommandResult<DashboardInvoiceStats>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<DashboardInvoiceStats>>(`${this.baseApiUrl}dashboard-stats`));
    }

    async getInvoiceStats(invoiceStatsFilterHttpParams: HttpParams): Promise<CommandResult<InvoiceStats>> {
        return await lastValueFrom(
            this.httpClient.get<CommandResult<InvoiceStats>>(`${this.baseApiUrl}stats`, { params: invoiceStatsFilterHttpParams }),
        );
    }

    async getInvoiceDetails(id: number): Promise<CommandResult<Invoice>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<Invoice>>(`${this.baseApiUrl}details/${id}`));
    }

    searchInvoicesForSinvoice(sinvoiceId: number, searchText: string): Observable<CommandResult<Invoice[]>> {
        return this.httpClient.get<CommandResult<Invoice[]>>(`${this.baseApiUrl}search-for-sinvoice/${sinvoiceId}/${searchText}`);
    }

    isInvoiceNumberUnique(number: string, id: number): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.baseApiUrl}/${number}/number-unique/${id}`);
    }

    async restoreFromDeleted(invoiceId: number): Promise<CommandResult<void>> {
        return await lastValueFrom(this.httpClient.put<CommandResult<void>>(`${this.baseApiUrl}restore-from-deleted/${invoiceId}`, null));
    }

    toString(object: Invoice): string {
        return object?.number;
    }
}
